import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import {
  useInsuranceCompaniesSupportedByProviderAndPatientQuery, useNonPartnerInsuranceCompanyQuery
} from '@uplift/queries';
import {
  Alert, DateOfBirthInput, InsuranceDropzone, InsuranceDropzoneError, LabelInfoTooltip, Select,
  StandaloneAddressQuickSearchForm, TextInput, YesNoRadioButtonGroup
} from '@uplift/ui-v2';

import { OTHER_NON_PARTNER_INSURANCE_ID } from '../../../utils';
import { InsuranceInfoFormValues } from './validation';

export type InsuranceInfoFormProps = {
  isInviteOrDirectFlow: boolean;
  providerId: string;
  insuranceId?: string;
  isBookingSponsored?: boolean;
  isPartnerInsurance?: boolean;
};

export const InsuranceInfoForm: React.VFC<InsuranceInfoFormProps> = ({
  providerId,
  insuranceId,
  isBookingSponsored = false,
  isPartnerInsurance,
}) => {
  const l10n = useL10n();
  const { classes, cx } = useInsuranceFormStyles();
  const formik = useFormikContext<InsuranceInfoFormValues>();
  const { errors, values } = formik;

  const isOtherNonPartnerInsuranceSelected = insuranceId === OTHER_NON_PARTNER_INSURANCE_ID;
  const { data: availableInsuranceCompanies } = useInsuranceCompaniesSupportedByProviderAndPatientQuery(providerId);
  const { data: nonPartnerInsuranceCompany } = useNonPartnerInsuranceCompanyQuery(insuranceId, { enabled: isBookingSponsored && !isPartnerInsurance && !isOtherNonPartnerInsuranceSelected });

  const insuranceCompanies = isBookingSponsored && nonPartnerInsuranceCompany && !isOtherNonPartnerInsuranceSelected
    ? [ { value: nonPartnerInsuranceCompany?.id, label: nonPartnerInsuranceCompany?.name } ]
    : availableInsuranceCompanies?.map(c => ({ value: c.id, label: c.name })) ?? [];

  const showAlert = useMemo(
    () => [ errors.insuranceCardBack, errors.insuranceCardFront ].some(x => x === InsuranceDropzoneError.FILE_TOO_LARGE),
    [ errors.insuranceCardBack, errors.insuranceCardFront ]
  );

  const primarySubscriberText = (
    <>
      <p>
        {l10n.matching.insurance.primarySubscriberTooltip1}
      </p>
      <p>
        {l10n.matching.insurance.primarySubscriberTooltip2}
      </p>
    </>
  );

  return (
    <div>
      <Box display="flex" flexDirection="column" gap="1.5rem">
        <Box>
          <YesNoRadioButtonGroup
            label={(
              <LabelInfoTooltip
                label={l10n.matching.insurance.arePrimarySubscriber}
                tooltipTitle={primarySubscriberText}
              />
            )}
            name="isPrimarySubscriber"
            asBoolean
          />
        </Box>
        <Box display="flex" flexDirection="column">
          {isOtherNonPartnerInsuranceSelected || nonPartnerInsuranceCompany?.creatorType === 'student' ? (
            <TextInput
              fullWidth
              id="insurance-provider-free-text"
              name="insuranceProviderFreeText"
              label={l10n.matching.insurance.insuranceProvider}
              placeholder="Free text for insurance wasn’t listed"
              className={classes.formControl75}
              autoComplete="off"
            />
          ) : (
            <Select
              name="insuranceProvider"
              label={l10n.matching.insurance.insuranceProvider}
              className={classes.formControl75}
              items={insuranceCompanies}
              disabled
              placeholder={(insuranceCompanies?.length) ? undefined : l10n.matching.insurance.noAvailableInsurances}
            />
          )}
        </Box>
        <TextInput
          fullWidth
          id="member-id"
          name="memberId"
          label={l10n.matching.insurance.memberId}
          className={cx(classes.formControl75, 'fs-exclude')}
        />
        <TextInput
          fullWidth
          id="group-number"
          name="groupNumber"
          label={l10n.matching.insurance.groupNumber}
          className={cx(classes.formControl75, 'fs-exclude')}
        />
        <Typography variant="overline" color="secondary" component="p">
          {l10n.matching.insurance.insuranceCard}
        </Typography>
        {showAlert && (
          <Alert className={classes.alert} severity="error">{l10n.accountSettings.insurance.insuranceCardDropRejected}</Alert>
        )}
        <InsuranceDropzone
          name="insuranceCardFront"
          placeholderText={l10n.matching.insurance.uploadFrontSide}
          altText="front side"
          color="secondary"
          isPartnerInsurance={isPartnerInsurance}
        />
        <InsuranceDropzone
          name="insuranceCardBack"
          placeholderText={l10n.matching.insurance.uploadBackSide}
          altText="back side"
          color="secondary"
          isPartnerInsurance={isPartnerInsurance}
        />
        {!values.isPrimarySubscriber && (
          <>
            <Typography variant="overline" color="secondary" component="p">
              {l10n.matching.insurance.primarySub}
            </Typography>
            <TextInput
              fullWidth
              id="primary-subscriber-first-name"
              name="primarySubscriberFirstName"
              label={l10n.onboarding.account.firstName}
              className={classes.formControl75}
            />
            <TextInput
              fullWidth
              id="primary-subscriber-last-name"
              name="primarySubscriberLastName"
              label={l10n.onboarding.account.lastName}
              className={classes.formControl75}
            />
            <DateOfBirthInput
              id="primary-subscriber-date-of-birth"
              name="primarySubscriberDateOfBirth"
              label={l10n.onboarding.account.dateOfBirth}
              helperText={l10n.matching.insurance.dateOfBirthTip}
              className={classes.dob}
              inputClasses={{ root: classes.dobInput }}
            />
            <StandaloneAddressQuickSearchForm
              label={l10n.matching.insurance.primarySubAddress}
              getFieldName={name => `address.${name}`}
            />
          </>
        )}
      </Box>
    </div>
  );
};

const useInsuranceFormStyles = makeStyles({ name: 'InsuranceForm' })(theme => ({
  link: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    textDecoration: 'underline',
    textTransform: 'none',
    '&:hover': {
      color: theme.palette.text.secondary,
      textDecoration: 'none',
    }
  },
  formControl75: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '75%',
    }
  },
  alert: {
    marginBottom: theme.spacing(1)
  },
  dob: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  dobInput: {
    width: '120px',
  }
}));
